import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Loader2, X } from "lucide-react";
import { signUpClientInvite } from "shared";
import { toast } from "sonner";
import { AuthSlider } from "@/components/auth/AuthSlider";
import { AuthCard } from "@/components/auth/auth-card";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Link } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import { useAppForm } from "@/hooks/useCreateForm";
import { authClient } from "@/lib/authClient";
export const Route = createFileRoute("/join/$id")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    context.queryClient.ensureQueryData(
      context.trpc.auth.session.queryOptions(),
    );
    context.queryClient.ensureQueryData(
      context.trpc.org.invite.queryOptions({
        inviteId: params.id,
      }),
    );
  },
});

// Handle the following situations
// 1. (Done) The user is not signed in and there is no account already for the invite
// 2. The user is not signed in and there is an account already for the invite (prompt to login)
// 3. (Done) The user is signed in and the invite is for them (prompt to accept)
// 4. (Done) The user is signed in and the invite is not for them (error)

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const { data: sessionData } = useQuery(trpc.auth.session.queryOptions());

  return (
    <div className="fixed inset-0 grid lg:grid-cols-[40fr_60fr]">
      <div className="flex flex-col gap-4 overflow-auto p-6 md:p-10">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-md">
            {sessionData?.user ? (
              <AuthenticatedJoinFlow />
            ) : (
              <UnauthenticatedJoinFlow />
            )}
          </div>
        </div>
      </div>
      <div className="relative hidden bg-gradient-to-r from-green-600 to-green-800 lg:block">
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <AuthSlider />
        </div>
      </div>
    </div>
  );
}

function AuthenticatedJoinFlow() {
  const { trpc } = Route.useRouteContext();
  const { id: inviteId } = Route.useParams();
  const navigate = useNavigate();
  const { queryClient } = Route.useRouteContext();

  const { data, error, isPending } = useQuery(
    trpc.org.invite.queryOptions(
      {
        inviteId,
      },
      {
        retry: false,
      },
    ),
  );

  const { mutate: respond, isPending: isResponding } = useMutation(
    trpc.org.respondToInvite.mutationOptions({
      onSuccess: async (result) => {
        if (result.status === "declined") {
          toast.info("Invitation declined");
        } else if (result.status === "accepted") {
          await queryClient.refetchQueries(trpc.org.list.queryOptions());
          toast.info("Invitation Accepted");
        }
        navigate({
          to: "/dashboard",
        });
      },
    }),
  );

  if (error) {
    toast.error("Invalid Invite", {
      description: error.message,
    });
    navigate({
      to: "/login",
    });
  }

  if (isPending || !data) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="mx-auto w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            Join Organization: {data.organizationName}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-card p-4">
            <div className="space-y-1">
              <p className="font-medium text-sm">Invitation expires:</p>
              <p className="text-muted-foreground text-sm">
                {new Date(data.expiresAt).toLocaleDateString("en-US")}
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2 sm:flex-row sm:justify-between sm:space-x-2 sm:space-y-0">
          <Button
            className="w-full sm:w-auto"
            disabled={isResponding}
            onClick={() =>
              respond({
                action: "decline",
                inviteId,
              })
            }
            variant="outline"
          >
            <X className="mr-2 h-4 w-4" />
            Decline
          </Button>
          <Button
            className="w-full sm:w-auto"
            disabled={isResponding}
            onClick={() =>
              respond({
                action: "accept",
                inviteId,
              })
            }
          >
            <Check className="mr-2 h-4 w-4" />
            Accept Invitation
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function UnauthenticatedJoinFlow() {
  const { trpc, queryClient } = Route.useRouteContext();
  const { id: inviteId } = Route.useParams();

  const {
    data: invite,
    error,
    isPending: inviteLoading,
  } = useQuery(
    trpc.org.invite.queryOptions({
      inviteId,
    }),
  );

  const { mutate: signUp, isPending: isSigningUp } = useMutation(
    trpc.auth.signUpInvite.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async (_data, vars) => {
        toast.success("Invitation Accepted");
        await authClient.signIn.email({
          email: vars.email,
          password: vars.password,
        });
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      confirm: "",
      email: invite?.email || "",
      enrollEmail: true,
      first: "",
      inviteId,
      last: "",
      password: "",
      phone: "",
    },
    onSubmit: async ({ value }) => {
      const { confirm: _confirm, ...rest } = value;
      signUp({
        ...rest,
        inviteId,
      });
    },
    validators: {
      onSubmit: signUpClientInvite,
    },
  });

  if (inviteLoading) {
    return (
      <AuthCard
        description="Please wait while we retrieve your invitation details."
        title="Loading invitation"
      >
        <div className="flex justify-center">
          <Loader2 className="animate-spin" />
        </div>
      </AuthCard>
    );
  }

  if (error || !invite) {
    return (
      <AuthCard
        description="This invitation link is invalid or has expired."
        title="Invalid invitation"
      >
        <div className="flex justify-end">
          <Link to="/login" variant="default">
            Go to login
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <AuthCard
        description={
          <Text as="span" variant="muted">
            You've been invited to join {invite.organizationName}. Create an
            account to accept the invitation. Already have an account?{" "}
            <Link to="/login" variant="default">
              Sign in
            </Link>
          </Text>
        }
        title={`Join ${invite.organizationName}`}
      >
        <Form>
          <div className="grid grid-cols-2 gap-3">
            <form.AppField
              children={(field) => {
                return (
                  <field.TextField
                    label="First"
                    placeholder="John"
                    required={true}
                  />
                );
              }}
              name="first"
            />
            <form.AppField
              children={(field) => {
                return (
                  <field.TextField
                    label="Last"
                    placeholder="Doe"
                    required={true}
                  />
                );
              }}
              name="last"
            />
          </div>

          <form.AppField
            children={(field) => {
              return (
                <field.TextField
                  disabled={true}
                  label="Email"
                  placeholder="example@gmail.com"
                  required={true}
                />
              );
            }}
            name="email"
          />
          <div className="grid grid-cols-2 gap-3">
            <form.AppField
              children={(field) => {
                return <field.PasswordField label="Password" required={true} />;
              }}
              name="password"
            />
            <form.AppField
              children={(field) => {
                return (
                  <field.PasswordField
                    label="Confirm Password"
                    required={true}
                  />
                );
              }}
              name="confirm"
            />
          </div>
          <form.AppField
            children={(field) => {
              return (
                <field.PhoneField label="Cell Phone Number" required={true} />
              );
            }}
            name="phone"
          />
          <form.AppField
            name="enrollEmail"
            children={(field) => {
              return (
                <field.CheckboxField
                  label={
                    <span className="text-muted-foreground text-sm">
                      Send me updates via email. Unsubscribe any time.
                    </span>
                  }
                />
              );
            }}
          />
          <span className="text-muted-foreground text-xs">
            I agree to receive recurring automated text messages at the phone
            number provided. Msg & data rates may apply. Msg frequency varies.
            Reply HELP for help and STOP to cancel. View our{" "}
            <a
              className="underline"
              href="https://www.givefront.com/legal/terms"
              rel="noopener noreferrer"
              target="_blank"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="underline"
              href="https://www.givefront.com/legal/privacy"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
          </span>
          <Button
            className="mt-2 w-full"
            disabled={isSigningUp}
            type="submit"
            variant="auth-primary"
          >
            {isSigningUp && <Loader2 className="mr-2 animate-spin" />}
            Create Account & Join {invite.organizationName}
          </Button>
        </Form>
      </AuthCard>
    </form>
  );
}
